// The demo user's ACT.SSO session, as the ACT Auth Portal hands it over. Its access token (the signed
// session) is sent as Authorization: Bearer on every /demo-api call (demoBackend.js), and the
// backend lists the user's jurisdictions.
//
// The portal keeps the token in a cookie shared across the parent domain (.advancedcompliance.tech).
// This page only reads it: it never calls the SSO API (no refresh) and never writes the cookie. The
// token's payload says who it belongs to and when it expires; the cookie is read again whenever the
// token is needed, so a token the portal or another product has renewed since is picked up.
// Without a valid token (the cookie is gone at its expiry, or the token in it has expired) the page
// goes to the portal's login: checked on load, before every /demo-api call, when the token expires
// and every 15 seconds. Signing out ends the
// session for every product, through the portal's logout.
//
// Every user change, sign-out and expiry starts a new "epoch": a response asked for in an earlier
// epoch belongs to the previous user and must not be shown.

const AUTH_URL = (process.env.REACT_APP_SSO_AUTH_URL || "").replace(/\/+$/, "");
const TOKEN_COOKIE = process.env.REACT_APP_SSO_TOKEN_COOKIE || "";
const REFRESH_COOKIE = process.env.REACT_APP_SSO_REFRESH_COOKIE || "";
const COOKIE_CHECK_MS = 15 * 1000;

export const Messages = {
  NOT_ACCEPTED: "The demo backend didn't accept your ACT session.",
  SSO_UNAVAILABLE: "ACT SSO is unavailable. Please try again shortly.",
  NO_PORTAL: "Open this page from the ACT portal to sign in.",
};

/**
 * @typedef {{
 *   status: "starting" | "redirecting" | "signedOut" | "signedIn",
 *   user: { name: string } | null,
 *   message: string | null,
 * }} SsoState
 * signedOut only when the page can't go to the portal (no portal is configured).
 */

/** @type {SsoState} */
let state = { status: "starting", user: null, message: null };
let session = null; // { token, userId, expiresAt (ms) }
let epoch = 0;
let expiryTimer = null;
let cookieCheck = null; // the 15-second look at the cookie, from the first valid token on
let started = false;
const listeners = new Set();

const setState = (next) => {
  state = { ...state, ...next };
  listeners.forEach((listener) => listener(state));
};

export const subscribe = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};
export const getState = () => state;
export const getEpoch = () => epoch;

// The portal's shared cookies --------------------------------------------------------------------

// The parent domain the portal sets them on (e.g. .advancedcompliance.tech), as the products do
const cookieDomain = () => {
  const { hostname } = window.location;
  const parts = hostname.split(".");
  return hostname === "localhost" || parts.length < 2 ? hostname : `.${parts.slice(-2).join(".")}`;
};

const readCookie = (name) => {
  if (!name) return null;
  for (const part of document.cookie.split(";")) {
    const [key, ...rest] = part.trim().split("=");
    if (key === name) return decodeURIComponent(rest.join("=")) || null;
  }
  return null;
};

const removeCookie = (name) => {
  if (!name) return;
  document.cookie = `${name}=; domain=${cookieDomain()}; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
  document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
};

// The token -------------------------------------------------------------------------------------

/**
 * The portal's token from its cookie, if it is still valid: { token, userId, name, expiresAt }.
 * The token is <base64 JSON payload>.<signature>; the payload is only read here (the backend checks
 * the signature), for who it belongs to and when it expires (unix seconds).
 */
const currentToken = () => {
  const token = readCookie(TOKEN_COOKIE);
  if (!token) return null;
  try {
    const payload = JSON.parse(atob(token.split(".")[0]));
    const expiresAt = Number(payload.expiresAt) * 1000;
    if (!payload.userId || !Number.isFinite(expiresAt) || expiresAt <= Date.now()) return null;
    return { token, userId: String(payload.userId), name: payload.userName || "ACT user", expiresAt };
  } catch {
    return null;
  }
};

const clearSession = () => {
  clearTimeout(expiryTimer);
  expiryTimer = null;
  session = null;
  epoch += 1;
};

// No valid token: sign in on the portal, which sends the user back here.
//
// An expired or unreadable token cookie comes off first. The portal counts anyone with that cookie
// as signed in without checking the token's expiry (and the cookie outlives it), so it would send
// the user straight back here with the same token: a redirect loop. Without it the portal shows its
// login. The refresh cookie stays, for the products that renew from it.
const toPortalLogin = () => {
  clearInterval(cookieCheck);
  cookieCheck = null;
  clearSession();
  removeCookie(TOKEN_COOKIE);
  if (!AUTH_URL) return setState({ status: "signedOut", user: null, message: Messages.NO_PORTAL });
  setState({ status: "redirecting", user: null, message: null });
  window.location.assign(`${AUTH_URL}/login?redirect=${encodeURIComponent(window.location.href)}`);
};

// When the token expires, look again: the portal or another product may have renewed it
const scheduleExpiry = () => {
  clearTimeout(expiryTimer);
  const scheduledIn = epoch;
  // setTimeout can't wait longer than ~24.8 days; a token never lives that long
  const delay = Math.min(Math.max(0, session.expiresAt - Date.now()), 2 ** 31 - 1);
  expiryTimer = setTimeout(() => {
    if (scheduledIn === epoch) getAccessToken();
  }, delay);
};

// Takes the token from the cookie. A different user (the portal signed someone else in) is a new
// session: a new epoch, so the previous user's state goes.
const adoptToken = (found) => {
  const sameUser = session?.userId === found.userId && state.status === "signedIn";
  if (!sameUser) clearSession();
  session = { token: found.token, userId: found.userId, expiresAt: found.expiresAt };
  scheduleExpiry();
  if (!cookieCheck) {
    cookieCheck = setInterval(() => {
      if (state.status === "signedIn") getAccessToken();
    }, COOKIE_CHECK_MS);
  }
  // The same user keeps the same object, so a renewed token doesn't look like a user change
  if (!sameUser) setState({ status: "signedIn", user: { name: found.name }, message: null });
};

/**
 * The current token for /demo-api, read from the portal's cookie; null (and the page goes to the
 * portal's login) when there is no valid one any more.
 */
export const getAccessToken = () => {
  const found = currentToken();
  if (!found) {
    if (state.status !== "redirecting") toPortalLogin();
    return null;
  }
  if (found.token !== session?.token) adoptToken(found);
  return found.token;
};

// The portal adds the user's profile as ?info= when it sends them here. The page doesn't use it (the
// token says who they are), so it comes off the address bar, and off any redirect= back to the portal.
const removeInfoParam = () => {
  const url = new URL(window.location.href);
  if (!url.searchParams.has("info")) return;
  url.searchParams.delete("info");
  window.history.replaceState(window.history.state, "", url.pathname + url.search + url.hash);
};

/** Picks up the portal's session from its cookie. Called once when the page loads. */
export const start = () => {
  if (started) return;
  started = true;
  removeInfoParam();
  getAccessToken();
};

/** Signs out of ACT: the portal's logout ends the session for every product. */
export const signOut = () => {
  clearInterval(cookieCheck);
  cookieCheck = null;
  clearSession();
  removeCookie(TOKEN_COOKIE);
  removeCookie(REFRESH_COOKIE);
  setState({ status: "redirecting", user: null, message: null });
  if (AUTH_URL) window.location.assign(`${AUTH_URL}/logout`);
};
